import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import client from '../api/client';

const plans = [
  {
    name: 'Free', planKey: 'free', price: '무료', yearPrice: '',
    color: '#8892A4', bg: '#161B27',
    features: ['쇼핑 순위 조회 1회/시간','추적 상품 3개','플레이스 등록 1개','키워드 검색량 5회/일','맞춤 마케팅 플랜 (제품 1개)','동시 접속 1기기'],
    disabled: ['자동 순위 추적','일일 이메일 리포트','PDF 저장'],
  },
  {
    name: 'Basic', planKey: 'basic', price: '월 13,900원', yearPrice: '연간 결제 시 월 11,800원 (15% 할인)',
    color: '#00D4AA', bg: '#0F2A22', badge: '',
    features: ['쇼핑 순위 조회 5회/시간','추적 상품 5개','플레이스 등록 2개','키워드 검색량 10회/일','맞춤 마케팅 플랜 (제품 3개)','자동 순위 추적 ✅','일일 이메일 리포트 ✅','PDF 저장 ✅','동시 접속 3기기'],
    disabled: [],
  },
  {
    name: 'Pro', planKey: 'pro', price: '월 29,900원', yearPrice: '연간 결제 시 월 23,900원 (20% 할인)',
    color: '#F6AD55', bg: '#2A1F0D', badge: '인기',
    features: ['쇼핑 순위 조회 10회/시간','추적 상품 10개','플레이스 등록 5개','키워드 검색량 무제한','맞춤 마케팅 플랜 무제한','자동 순위 추적 ✅','일일 이메일 리포트 ✅','PDF 저장 ✅','동시 접속 5기기','전담 고객 지원'],
    disabled: [],
  },
];

const PLAN_LEVEL: Record<string, number> = { free: 0, basic: 1, pro: 2 };

function PricingContent({ navigate, userPlan, subStatus, onReactivate }: {
  navigate: any; userPlan: string; subStatus: any; onReactivate: () => void;
}) {
  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const isCancelled = subStatus?.status === 'cancelled';

  const getBtn = (planKey: string) => {
    if (!isLoggedIn) return { label: '무료로 시작하기', disabled: false, action: 'register' };

    const currentLevel = PLAN_LEVEL[userPlan] || 0;
    const targetLevel = PLAN_LEVEL[planKey] || 0;

    // Free 카드
    if (planKey === 'free') {
      if (userPlan === 'free') return { label: '현재 이용 중', disabled: true, action: '' };
      return { label: '하위 플랜', disabled: true, action: '' };
    }

    // 현재 플랜과 동일
    if (userPlan === planKey) {
      if (isCancelled) return { label: '구독 재활성화', disabled: false, action: 'reactivate' };
      return { label: '현재 구독 중', disabled: true, action: '' };
    }

    // 하위 플랜 (취소 예정 아닐 때)
    if (targetLevel < currentLevel && !isCancelled) {
      return { label: '하위 플랜', disabled: true, action: '' };
    }

    // 상위 플랜 또는 취소 예정
    return { label: isCancelled ? '첫 달 무료로 재구독' : '업그레이드', disabled: false, action: 'checkout' };
  };

  const handleClick = async (planKey: string, action: string) => {
    if (action === 'register') {
      navigate('/register');
    } else if (action === 'reactivate') {
      onReactivate();
    } else if (action === 'checkout') {
      navigate(`/checkout?plan=${planKey}`);
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <h1 style={{ color: '#E8ECF4', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>플랜 & 요금제</h1>
        <div style={{ background: '#0F2A22', border: '1px solid #00D4AA55', borderRadius: 10, padding: '12px 24px', marginBottom: 16, display: 'inline-block' }}>
          <span style={{ color: '#00D4AA', fontSize: 15, fontWeight: 700 }}>✅ 모든 기능 무료로 시작!</span>
          <span style={{ color: '#8892A4', fontSize: 13, marginLeft: 8 }}>결제 없이 시작 · 한도 초과 시 유료 플랜 · 첫 달 무료 체험 가능</span>
        </div>
        <p style={{ color: '#8892A4', fontSize: 14 }}>모든 플랜은 언제든지 변경하거나 취소할 수 있습니다</p>
      </div>

      {isCancelled && (
        <div style={{ background: '#1A1500', border: '0.5px solid #F6AD5544', borderRadius: 10, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: '#F6AD55', fontSize: 13, fontWeight: 700, marginBottom: 2 }}>⚠️ 구독 취소 예정</div>
            <div style={{ color: '#8892A4', fontSize: 12 }}>
              {subStatus?.expires_at ? new Date(subStatus.expires_at).toLocaleDateString('ko-KR') : ''}까지 이용 가능하며 이후 Free로 전환됩니다.
            </div>
          </div>
          <button onClick={onReactivate} style={{ background: '#F6AD55', border: 'none', borderRadius: 8, padding: '8px 20px', color: '#0A1F18', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            구독 재활성화
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 40 }}>
        {plans.map(plan => {
          const btn = getBtn(plan.planKey);
          return (
            <div key={plan.name} style={{ background: plan.bg, border: `1px solid ${plan.color}44`, borderRadius: 12, padding: 28, position: 'relative' as const }}>
              {plan.badge && <div style={{ position: 'absolute' as const, top: -10, right: 20, background: plan.color, color: '#0A1F18', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 99 }}>{plan.badge}</div>}
              {isLoggedIn && userPlan === plan.planKey && !isCancelled && (
                <div style={{ position: 'absolute' as const, top: -10, left: 20, background: '#378ADD', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 99 }}>현재 플랜</div>
              )}
              <div style={{ color: plan.color, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ color: '#E8ECF4', fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{plan.price}</div>
              <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 20, minHeight: 16 }}>{(plan as any).yearPrice || ''}</div>
              <button
                disabled={btn.disabled}
                onClick={() => handleClick(plan.planKey, btn.action)}
                style={{
                  width: '100%', border: 'none', borderRadius: 8, padding: '12px',
                  background: btn.disabled ? '#1A2035' : plan.planKey === 'free' ? '#1A2035' : plan.color,
                  color: btn.disabled ? '#4A5568' : plan.planKey === 'free' ? '#4A5568' : '#0A1F18',
                  fontSize: 14, fontWeight: 700,
                  cursor: btn.disabled ? 'default' : 'pointer', marginBottom: 24
                }}>
                {btn.label}
              </button>
              <div style={{ borderTop: `0.5px solid ${plan.color}33`, paddingTop: 16 }}>
                {plan.features.map(f => (
                  <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <span style={{ color: plan.color, fontSize: 12, marginTop: 1, flexShrink: 0 }}>✓</span>
                    <span style={{ color: '#E8ECF4', fontSize: 13 }}>{f}</span>
                  </div>
                ))}
                {plan.disabled.map(f => (
                  <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <span style={{ color: '#2A3045', fontSize: 12, flexShrink: 0 }}>✗</span>
                    <span style={{ color: '#4A5568', fontSize: 13 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 12, padding: 28 }}>
        <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>자주 묻는 질문</div>
        {[
          { q: '구독을 취소하면 바로 이용이 중단되나요?', a: '아닙니다. 취소 후에도 현재 구독 기간 만료일까지 정상 이용 가능합니다. 만료일 이후 자동 갱신이 중단되고 Free 플랜으로 전환됩니다.' },
          { q: '자동 갱신은 언제 결제되나요?', a: '첫 가입 후 30일은 무료로 이용하실 수 있습니다. 무료 기간 종료 후 등록하신 카드로 자동 결제되며, 이후 매월(또는 매년) 자동 갱신됩니다. 원하지 않으시면 무료 기간 내 마이페이지에서 취소해 주세요.' },
          { q: '연간 구독 환불이 가능한가요?', a: '결제 후 7일 이내 전액 환불 가능합니다 (전자상거래법). 7일 초과 시 환불이 불가하며, 구독 취소 후 만료일까지 정상 이용 가능합니다.' },
          { q: '플랜을 중간에 변경할 수 있나요?', a: '언제든지 상위 플랜으로 업그레이드 가능합니다. 잔여 기간을 일할 계산하여 차액만 결제하시면 즉시 적용됩니다.' },
          { q: '결제 실패 시 어떻게 되나요?', a: '자동 갱신 결제 실패 시 다음 날 자동으로 재시도합니다. 3회 연속 실패 시 Free 플랜으로 전환되며, 매 실패마다 안내 이메일이 발송됩니다.' },
          { q: '동시 접속 제한이란?', a: '하나의 계정으로 동시에 접속 가능한 기기 수를 제한합니다. 초과 시 가장 오래된 세션이 자동 로그아웃됩니다.' },
        ].map(item => (
          <div key={item.q} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '0.5px solid #1E2535' }}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Q. {item.q}</div>
            <div style={{ color: '#8892A4', fontSize: 13, lineHeight: 1.6 }}>A. {item.a}</div>
          </div>
        ))}
      </div>
      {/* 서비스 상세 설명 */}
      <div style={{ marginTop: 32 }}>
        <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700, marginBottom: 20, textAlign: 'center' as const }}>셀러랭키 제공 서비스</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            {
              title: '🛒 쇼핑 마케팅 도구',
              items: [
                '쇼핑 순위 조회 - 상품이 특정 키워드에서 네이버 쇼핑 몇 페이지 몇 위인지 실시간 확인',
                '순위 변동 추적 - 키워드별 순위 변화를 그래프로 시각화, 급변동 알림 및 매일 이메일 리포트',
                '통합검색 분석(다중) - 여러 키워드를 한번에 입력해 네이버 통합검색 노출 현황 분석',
                '통합검색 분석(수집) - 특정 키워드의 네이버 통합검색 결과 자동 수집 및 분석',
                '경쟁사 비교 - 경쟁 상품과 키워드별 순위 및 상품명 SEO 점수 비교',
              ]
            },
            {
              title: '🔍 키워드 분석 도구',
              items: [
                '키워드 검색량(다중) - 최대 50개 키워드를 한번에 입력해 월간 검색량, 경쟁도, 클릭률 분석',
                '키워드 검색량(연관) - 키워드 1개로 연관 키워드 최대 200개의 검색량 분석',
                '키워드 세부분석 - 검색 트렌드, 성별/연령대 비중, PC/모바일 비중 시각화',
                '형태소 분석기 - 상품명/키워드의 형태소 분석으로 SEO 최적화 지원',
              ]
            },
            {
              title: '📍 플레이스 마케팅 도구',
              items: [
                '플레이스 순위 조회 - 네이버 플레이스(지도) 검색 시 업체 순위 실시간 조회 (최대 100위)',
                '순위 변동 추적 - 플레이스 키워드별 순위 변화 추적 및 그래프 시각화',
                '경쟁사 분석/비교 - 경쟁 업체와 키워드별 순위 비교 분석',
              ]
            },
            {
              title: '📊 마케팅 전략 도구',
              items: [
                'Step 1. 관여도 진단 - 상품/서비스의 고관여/저관여 수준 진단',
                'Step 2. 마케팅 전략 - 진단 결과 기반 맞춤형 마케팅 전략 수립',
                'Step 3. 키워드 전략 - 분석 데이터 기반 키워드 전략 수립',
                'Step 4. 전략 리포트 - 전체 분석 결과를 종합한 마케팅 리포트 생성',
              ]
            },
          ].map(section => (
            <div key={section.title} style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 12, padding: 24 }}>
              <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 700, marginBottom: 14 }}>{section.title}</div>
              {section.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <span style={{ color: '#00D4AA', fontSize: 12, flexShrink: 0, marginTop: 2 }}>✓</span>
                  <span style={{ color: '#8892A4', fontSize: 12, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [userPlan, setUserPlan] = useState('free');
  const [subStatus, setSubStatus] = useState<any>(null);

  useEffect(() => {
    if (isLoggedIn) {
      client.get('/auth/me').then(r => setUserPlan(r.data.plan || 'free')).catch(() => {});
      client.get('/payment/billing/status').then(r => setSubStatus(r.data)).catch(() => {});
    }
  }, []);

  const handleReactivate = async () => {
    if (!window.confirm('구독을 재활성화하시겠습니까?\n기존 만료일 이후 자동 갱신됩니다.')) return;
    try {
      const r = await client.post('/payment/billing/reactivate');
      alert(r.data.message);
      window.location.reload();
    } catch (e: any) {
      alert(e.response?.data?.detail || '재활성화 실패');
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={{ background: '#0F1117', minHeight: '100vh', color: '#E8ECF4' }}>
        <nav style={{ borderBottom: '0.5px solid #1E2535', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0F1117' }}>
          <div onClick={() => navigate('/')} style={{ color: '#00D4AA', fontSize: 20, fontWeight: 800, cursor: 'pointer' }}>SellerRanky</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ background: 'none', border: '1px solid #2A3045', borderRadius: 8, padding: '8px 20px', color: '#E8ECF4', cursor: 'pointer' }}>로그인</button>
            <button onClick={() => navigate('/register')} style={{ background: '#00D4AA', border: 'none', borderRadius: 8, padding: '8px 20px', color: '#0A1F18', fontWeight: 700, cursor: 'pointer' }}>무료로 시작하기</button>
          </div>
        </nav>
        <div style={{ padding: '40px 24px' }}>
          <PricingContent navigate={navigate} userPlan="free" subStatus={null} onReactivate={handleReactivate} />
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <PricingContent navigate={navigate} userPlan={userPlan} subStatus={subStatus} onReactivate={handleReactivate} />
    </Layout>
  );
}
